import { useMemo, useState } from "react";
import { useSubmitLineup } from "../lib/api/leagues";
import type { LineupPick } from "../lib/types";

interface LineupBuilderProps {
	leagueId: string;
	theme: string;
	roster: readonly string[];
	onSubmitted?: () => void;
}

// LineupBuilder — 10-row symbol-picker + allocation% input grid. Constrained
// to a theme's static roster (passed in from the parent route, which sources
// from lib/api/leagues.ts::THEME_ROSTERS). Client-side hint when the
// running-total != 100%; the server (fantasy svc ValidateLineup) is the
// source of truth and rejects with `lineup_bad_allocation` if it disagrees.

const SIZE = 10;

interface RowState {
	symbol: string;
	allocationPct: string;
}

const blankRow = (): RowState => ({ symbol: "", allocationPct: "" });

export function LineupBuilder({
	leagueId,
	theme,
	roster,
	onSubmitted,
}: LineupBuilderProps) {
	const [rows, setRows] = useState<RowState[]>(() =>
		Array.from({ length: SIZE }, blankRow),
	);
	const submit = useSubmitLineup(leagueId);

	// Build the set of already-chosen symbols so each row's <select> can
	// dim options that other rows have already claimed.
	const chosen = useMemo(() => {
		const m = new Map<string, number>();
		rows.forEach((r, i) => {
			if (r.symbol) m.set(r.symbol, i);
		});
		return m;
	}, [rows]);

	const total = useMemo(
		() =>
			rows.reduce((acc, r) => {
				const n = parseFloat(r.allocationPct);
				return acc + (Number.isFinite(n) ? n : 0);
			}, 0),
		[rows],
	);
	const totalRounded = Math.round(total * 100) / 100;
	const sumOk = Math.abs(total - 100) < 0.01;
	const allFilled = rows.every(
		(r) => r.symbol.length > 0 && r.allocationPct.length > 0,
	);
	const noDupes = chosen.size === rows.filter((r) => r.symbol).length;

	function setRow(i: number, patch: Partial<RowState>) {
		setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		const picks: LineupPick[] = rows.map((r) => ({
			symbol: r.symbol.trim().toUpperCase(),
			allocationPct: parseFloat(r.allocationPct) || 0,
		}));
		try {
			await submit.mutateAsync(picks);
			onSubmitted?.();
		} catch {
			// ApiClientError surfaced via submit.error below.
		}
	}

	const canSubmit = allFilled && noDupes && sumOk && !submit.isPending;

	const errorMsg = (() => {
		if (!submit.error) return null;
		const e = submit.error;
		// Surface the wire-stable code so PMs can debug a rejection without
		// reading the message.
		return `${e.code}: ${e.message}`;
	})();

	return (
		<form onSubmit={onSubmit} className="space-y-3">
			<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
				<div className="grid grid-cols-[2rem_1fr_8rem] items-center gap-2 border-b border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-muted)]">
					<span>#</span>
					<span>Symbol</span>
					<span className="text-right">Allocation %</span>
				</div>
				{rows.map((row, i) => {
					const duplicate = row.symbol && (chosen.get(row.symbol) ?? i) !== i;
					return (
						<div
							key={i}
							className="grid grid-cols-[2rem_1fr_8rem] items-center gap-2 border-b border-[var(--color-border)] px-3 py-2 last:border-b-0"
						>
							<span className="text-xs text-[var(--color-muted)]">
								{i + 1}
							</span>
							<select
								value={row.symbol}
								onChange={(e) => setRow(i, { symbol: e.target.value })}
								className={`rounded-md border bg-[var(--color-bg-elev)] px-2 py-1 text-sm font-mono ${
									duplicate
										? "border-[var(--color-danger)]"
										: "border-[var(--color-border)]"
								}`}
							>
								<option value="">— pick a symbol —</option>
								{roster.map((sym) => (
									<option key={sym} value={sym}>
										{sym}
									</option>
								))}
							</select>
							<input
								type="number"
								step="0.01"
								min="0"
								max="100"
								inputMode="decimal"
								value={row.allocationPct}
								onChange={(e) =>
									setRow(i, { allocationPct: e.target.value })
								}
								className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-2 py-1 text-right text-sm"
								placeholder="0.00"
							/>
						</div>
					);
				})}
			</div>

			<div className="flex items-center justify-between text-xs">
				<span className="text-[var(--color-muted)]">
					Theme: <span className="text-[var(--color-fg)]">{theme}</span>
				</span>
				<span
					className={
						sumOk
							? "text-[var(--color-accent)]"
							: "text-[var(--color-danger)]"
					}
				>
					Total: {totalRounded.toFixed(2)}% / 100%
				</span>
			</div>

			{!noDupes && (
				<p className="text-xs text-[var(--color-danger)]">
					Duplicate symbols — each pick must be unique.
				</p>
			)}

			{errorMsg && <p className="text-xs text-[var(--color-danger)]">{errorMsg}</p>}

			<button
				type="submit"
				disabled={!canSubmit}
				className="w-full rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
			>
				{submit.isPending ? "Submitting…" : "Submit lineup"}
			</button>
		</form>
	);
}
